import { useQuery } from "@apollo/client";
import { groupBy, mapValues } from "lodash";
import { useParams } from "react-router-dom";

import { graphql } from "@/gql";
import { MealRatingByDateRange } from "@/gql/graphql";
import { formatISODate } from "@/utils/datetime";

import { ClientReportPageParams, RatingReport } from "./types";
import { weeklyDates } from "./useWeeklyRatingGraph";

export const mealRatingReportQuery = graphql(`
  query MealRatingReport(
    $clientId: ID!
    $startDate: ISO8601Date!
    $endDate: ISO8601Date!
  ) {
    mealRatingByDateRange(
      clientId: $clientId
      startDate: $startDate
      endDate: $endDate
    ) {
      rating
      mealType
      totalRating
    }
  }
`);

export const useMealRatingReport = (
  startDate?: string,
  endDate?: string
): Record<string, RatingReport[]> => {
  const { clientId, date } = useParams<ClientReportPageParams>();

  const dates = weeklyDates(date);
  const initialStartDate = formatISODate(dates[0]);
  const initialEndDate = formatISODate(dates[6]);

  const { data } = useQuery(mealRatingReportQuery, {
    variables: {
      clientId: clientId ?? "",
      startDate: startDate || initialStartDate,
      endDate: endDate || initialEndDate,
    },
  });

  if (data?.mealRatingByDateRange.length == 0) return {};

  // Group data according to mealType
  const groupedMealRatingReport = groupBy(
    data?.mealRatingByDateRange,
    "mealType"
  );

  // Modified the grouped data and assign zero value for ratings that have no totalRating
  const mealRatingReport = mapValues(groupedMealRatingReport, (rating) =>
    addMissingRating(rating)
  );

  return mealRatingReport;
};

/**
 * Checks missing ratings from [mealRatingReport]
 * @param mealRatingReport an array of MealRatingByDateRange
 * @returns an array of RatingReport
 */
export const addMissingRating = (
  mealRatingReport: MealRatingByDateRange[]
): RatingReport[] => {
  const ratings = [5, 4, 3, 2, 1];

  const modifiedRatingReport: RatingReport[] = [];

  ratings.forEach((rating) => {
    const existingRating = mealRatingReport.find(
      (report) => report.rating === rating
    );

    modifiedRatingReport.push(existingRating ?? { rating, totalRating: 0 });
  });

  return modifiedRatingReport;
};
